import {
  CHECK_TYPE_KEYS,
  CHECK_TYPE_LABELS,
  type CheckTypeKey,
  type LimitPeriod,
  type PaymentPeriod,
  type Pricelist,
  type PricelistStatus,
  type PriceItem,
} from "../api/pricelist";

export const PAYMENT_PERIOD_OPTIONS: { value: PaymentPeriod; label: string }[] = [
  { value: "month", label: "Месяц" },
  { value: "quarter", label: "Квартал" },
  { value: "year", label: "Год" },
];

export const LIMIT_PERIOD_OPTIONS: { value: LimitPeriod; label: string }[] = [
  { value: "day", label: "День" },
  { value: "week", label: "Неделя" },
  { value: "month", label: "Месяц" },
  { value: "year", label: "Год" },
];

export const PRICELIST_STATUS_OPTIONS: { value: PricelistStatus; label: string }[] = [
  { value: "active", label: "Активный" },
  { value: "archived", label: "В архиве" },
];

const PAYMENT_PERIOD_ORDER: Record<PaymentPeriod, number> = {
  month: 0,
  quarter: 1,
  year: 2,
};

const normalizePaymentPeriod = (period: string): PaymentPeriod | null => {
  if (period === "month" || period === "quarter" || period === "year") return period;
  return null;
};

const sortByPaymentPeriod = <T extends { period: PaymentPeriod }>(items: T[]): T[] =>
  [...items].sort(
    (a, b) =>
      (PAYMENT_PERIOD_ORDER[a.period] ?? Number.MAX_SAFE_INTEGER) -
      (PAYMENT_PERIOD_ORDER[b.period] ?? Number.MAX_SAFE_INTEGER)
  );

export const getPaymentPeriodLabel = (period: PaymentPeriod | string): string =>
  PAYMENT_PERIOD_OPTIONS.find((item) => item.value === period)?.label ?? period;

export const getLimitPeriodLabel = (period: LimitPeriod | string): string =>
  LIMIT_PERIOD_OPTIONS.find((item) => item.value === period)?.label ?? period;

export const getPricelistStatusLabel = (status: PricelistStatus | string | undefined): string =>
  PRICELIST_STATUS_OPTIONS.find((item) => item.value === status)?.label ?? status ?? "—";

export const formatPricelistPrice = (price: number): string =>
  new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(price);

export const formatSiteAmount = (value?: number | null): string => {
  if (value == null || !Number.isFinite(value)) return "—";
  const n = Math.abs(Math.trunc(value));
  const mod10 = n % 10;
  const mod100 = n % 100;
  let word = "сайтов";
  if (mod100 < 11 || mod100 > 14) {
    if (mod10 === 1) word = "сайт";
    else if (mod10 >= 2 && mod10 <= 4) word = "сайта";
  }
  return `${n} ${word}`;
};

export const getPricelistPriceItems = (item: Pricelist): PriceItem[] => {
  const fromPrice = item.price?.periods;
  const fromList = item.priceItemList;
  const source = fromPrice?.length ? fromPrice : fromList ?? [];

  return sortByPaymentPeriod(
    source.flatMap((priceItem) => {
      const period = normalizePaymentPeriod(String(priceItem.period));
      if (!period || priceItem.price == null || !Number.isFinite(priceItem.price)) return [];
      return [{ period, price: priceItem.price }];
    })
  );
};

export type CategoryNameResolver = (categoryId: string) => string;

const getEnabledCheckTypes = (item: Pricelist): CheckTypeKey[] => {
  if (item.checkTypes) {
    return CHECK_TYPE_KEYS.filter((key) => item.checkTypes?.[key]?.enabled === true);
  }

  const legacy: CheckTypeKey[] = [];
  if ((item.tariffItemList?.length ?? 0) > 0 || (item.siteAmount ?? 0) > 0) {
    legacy.push("siteCheck");
  }
  if (item.policyCheckEnabled === true) {
    legacy.push("policyCheck");
  }
  return legacy;
};

export const getPricelistCheckTypeLabels = (item: Pricelist): string[] => {
  const enabled = getEnabledCheckTypes(item);
  return enabled.length ? enabled.map((key) => CHECK_TYPE_LABELS[key]) : ["—"];
};

export const getPricelistCheckTypeLimits = (item: Pricelist): string[] => {
  if (item.checkTypes) {
    const lines = CHECK_TYPE_KEYS.flatMap((key) => {
      const config = item.checkTypes?.[key];
      if (!config?.enabled) return [];
      const limits = Array.isArray(config.limits) ? config.limits : [];
      if (!limits.length) {
        const legacy = Number((config as { limit?: number }).limit);
        if (Number.isFinite(legacy) && legacy > 0) {
          return [`${CHECK_TYPE_LABELS[key]}: ${legacy}`];
        }
        return [`${CHECK_TYPE_LABELS[key]}: —`];
      }
      return limits.map((usage) => {
        const count =
          Number((usage as { count?: number }).count) ||
          Number((usage as { limit?: number }).limit);
        return `${CHECK_TYPE_LABELS[key]}: ${
          Number.isFinite(count) ? count : "—"
        } / ${getPaymentPeriodLabel(usage.period)}`;
      });
    });
    return lines.length ? lines : ["—"];
  }

  if (item.policyCheckEnabled === true) {
    const limits = item.policyCheckSettings?.usageSettings ?? [];
    return limits.length
      ? limits.map((usage) => `${usage.limit} / ${usage.period}`)
      : ["—"];
  }

  const limits = item.tariffItemList ?? [];
  return limits.length
    ? limits.map((tariff) => `${tariff.limit} / ${tariff.period}`)
    : ["—"];
};

export const getPricelistCheckTypeCategories = (
  item: Pricelist,
  resolveCategoryName: CategoryNameResolver
): string[] => {
  if (item.checkTypes) {
    const lines = CHECK_TYPE_KEYS.flatMap((key) => {
      const config = item.checkTypes?.[key];
      if (!config?.enabled) return [];
      const names = (config.categoryIds ?? [])
        .map((id) => resolveCategoryName(id))
        .filter((name) => name && name !== "—");
      if (!names.length) return [`${CHECK_TYPE_LABELS[key]}: —`];
      return [`${CHECK_TYPE_LABELS[key]}: ${names.join(", ")}`];
    });
    return lines.length ? lines : ["—"];
  }

  if (item.policyCheckEnabled === true) return ["—"];

  const categoryIds = [
    ...new Set(item.tariffItemList?.map((tariff) => tariff.categoryId).filter(Boolean) ?? []),
  ];
  const names = categoryIds
    .map((id) => resolveCategoryName(id))
    .filter((name) => name && name !== "—");
  return names.length ? names : ["—"];
};

export const getPricelistCheckTypeOptions = (item: Pricelist): string[] => {
  if (!item.checkTypes) {
    const options: string[] = [];
    if (item.scanByScheduleEnabled === true) options.push("Расписание");
    if (item.emailNotification === true) options.push("Отчёт");
    return options.length ? options : ["—"];
  }

  const lines = CHECK_TYPE_KEYS.flatMap((key) => {
    const config = item.checkTypes?.[key];
    if (!config?.enabled) return [];
    const parts: string[] = [];
    if (config.options?.schedule) parts.push("расписание");
    if (config.options?.report) parts.push("отчёт");
    return [`${CHECK_TYPE_LABELS[key]}: ${parts.length ? parts.join(", ") : "—"}`];
  });
  return lines.length ? lines : ["—"];
};

export const getPricelistVisibleForUsersLabel = (item: Pricelist): string =>
  item.visibleForUsers === false ? "Нет" : "Да";

export const getPricelistPrices = (item: Pricelist): string[] => {
  const sorted = getPricelistPriceItems(item);
  return sorted.length ? sorted.map((priceItem) => formatPricelistPrice(priceItem.price)) : ["—"];
};

export const getPricelistPaymentPeriods = (item: Pricelist): string[] => {
  const sorted = getPricelistPriceItems(item);
  return sorted.length
    ? sorted.map((priceItem) => getPaymentPeriodLabel(priceItem.period))
    : ["—"];
};
